import { Role, UserRole } from 'types/global';

/**
 * Selects the roles a user already holds that this form is allowed to manage.
 *
 * A user can be granted roles through other back-end APIs — internal roles that never appear in
 * this form's picker. Those must stay hidden here and be left out of the update payload, so the
 * API preserves them rather than reading their absence as a removal.
 * @param {Array<UserRole> | undefined} userRoles - Role assignments the user currently holds
 * @param {Role[]} assignableRoles - Roles the picker offers in the current client context
 * @returns {Role[]} The held roles the picker also offers, in the order they were held
 * @example
 * // Nike admin editing a user who was granted an internal role elsewhere
 * selectAssignableUserRoles(
 *   [{ role: { id: 4, name: 'Vantage Basic' } }, { role: { id: 32, name: 'New Deal Early Release' } }],
 *   [{ id: 1, name: 'Account Administrator' }, { id: 4, name: 'Vantage Basic' }]
 * ); // => [{ id: 4, name: 'Vantage Basic' }]
 */
export const selectAssignableUserRoles = (userRoles: Array<UserRole> | undefined, assignableRoles: Role[]): Role[] => {
  const assignableIds = new Set(assignableRoles.map(({ id }) => id));
  return userRoles?.map(({ role }) => role).filter((role): role is Role => !!role && assignableIds.has(role.id)) || [];
};
